const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const normalizeBearing = (value) => {
  if (!Number.isFinite(value)) return undefined;
  const normalized = ((value % 360) + 360) % 360;
  return Number(normalized.toFixed(1));
};

export const DEFAULT_MAP_CENTER = [-86.7911, 32.8067];
export const DEFAULT_MAP_ZOOM = 6;
export const DEFAULT_MAP_PITCH = 0;
export const DEFAULT_MAP_BEARING = 0;
export const MAP_VIEW_TTL_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

const clampLng = (value) => clamp(value, -180, 180);
const clampLat = (value) => clamp(value, -90, 90);

export function sanitizeMapView(rawView) {
  if (!rawView || typeof rawView !== 'object') return null;
  const centerArray = Array.isArray(rawView.center) ? rawView.center : null;
  if (!centerArray || centerArray.length !== 2) return null;

  const lng = Number(centerArray[0]);
  const lat = Number(centerArray[1]);
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;

  const zoom = Number(rawView.zoom);
  const pitch = Number(rawView.pitch);
  const bearing = Number(rawView.bearing);

  return {
    center: [clampLng(lng), clampLat(lat)],
    zoom: Number.isFinite(zoom) ? clamp(zoom, 0, 22) : undefined,
    pitch: Number.isFinite(pitch) ? clamp(pitch, 0, 85) : undefined,
    bearing: Number.isFinite(bearing) ? normalizeBearing(bearing) : undefined
  };
}

export function createPersistableMapView(input) {
  if (!input || typeof input !== 'object') return null;
  const rawCenter = input.center;
  let lng;
  let lat;

  if (rawCenter && typeof rawCenter.lng === 'number' && typeof rawCenter.lat === 'number') {
    lng = rawCenter.lng;
    lat = rawCenter.lat;
  } else if (Array.isArray(rawCenter) && rawCenter.length === 2) {
    lng = Number(rawCenter[0]);
    lat = Number(rawCenter[1]);
  } else if (typeof input.lng === 'number' && typeof input.lat === 'number') {
    lng = input.lng;
    lat = input.lat;
  } else {
    return null;
  }

  const view = sanitizeMapView({
    center: [lng, lat],
    zoom: input.zoom,
    pitch: input.pitch,
    bearing: input.bearing
  });

  if (!view) return null;

  return {
    ...view,
    // extra rounding to keep storage payloads tidy
    center: [Number(view.center[0].toFixed(6)), Number(view.center[1].toFixed(6))],
    zoom: view.zoom !== undefined ? Number(view.zoom.toFixed(2)) : undefined,
    pitch: view.pitch !== undefined ? Number(view.pitch.toFixed(1)) : undefined,
    bearing: view.bearing !== undefined ? Number(view.bearing.toFixed(1)) : undefined
  };
}

export function normalizePersistedMapView(rawValue, now = Date.now()) {
  if (!rawValue) return null;
  const viewPayload = typeof rawValue.view === 'object' ? rawValue.view : rawValue;
  const savedAt = typeof rawValue.savedAt === 'number' ? rawValue.savedAt : null;
  const view = sanitizeMapView(viewPayload);
  if (!view) return null;
  const isFresh = savedAt != null ? (now - savedAt) < MAP_VIEW_TTL_MS : false;
  return { view, savedAt, isFresh };
}

